import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { isNil } from 'ramda';
import { z } from 'zod';

import { logEvent } from 'features/Analytics/analytics';

import Component from './component';
import {
  getCurrentUserBeneficiary,
  getLegalBeneficiariesWithoutCurrentUser,
  getTotalBeneficiaries,
  hasAccountHolderInvitation,
} from './helpers';
import locales from './locales';
import {
  getDeletedSupposedBeneficiaries,
  saveDeletedSupposedBeneficiaries,
} from './storage';
import {
  GetIsAddedAmountSharesValidFn,
  ManageBeneficiariesPageProps,
} from './types';

const formSchema = z.object({
  hasAccountHolder: z.literal<boolean>(true).optional(),
  isDoneAddingBeneficiaries: z.literal<boolean>(true, {
    errorMap: () => ({
      message: locales.confirmationTextError,
    }),
  }),
});

export type FormSchema = z.input<typeof formSchema>;

const ManageBeneficiariesPage: FC<ManageBeneficiariesPageProps> = ({
  applicantType,
  applicationType,
  data,
  header,
  includeShares = false,
  isExistingCompany,
  isSaving,
  onContinue,
  onMoralBeneficiaryPress,
}) => {
  const [isCreatingBeneficiary, setIsCreatingBeneficiary] = useState(false);
  const methods = useForm({
    defaultValues: {
      isDoneAddingBeneficiaries: false,
    },
    mode: 'onChange',
    resolver: zodResolver(formSchema),
  });

  const totalBeneficiaries = getTotalBeneficiaries(data);

  const [deletedSupposedBeneficiaries, setDeletedSupposedBeneficiaries] =
    useState(getDeletedSupposedBeneficiaries());

  const supposedBeneficiaries = isExistingCompany
    ? data.company.supposedBeneficiaries.filter(({ supposedBeneficiaryId }) => {
        return !deletedSupposedBeneficiaries.has(supposedBeneficiaryId);
      })
    : [];

  useEffect(() => {
    logEvent({
      name: 'Onboarding Beneficiary List Displayed',
      properties: {
        totalBeneficiaries,
        totalSuggestedBeneficiaries: supposedBeneficiaries.length,
      },
      usePrefix: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const companyLegalBeneficiaries =
    getLegalBeneficiariesWithoutCurrentUser(data);
  const currentUserBeneficiary = getCurrentUserBeneficiary(data);

  const onAddAnother = () => {
    setIsCreatingBeneficiary(true);

    logEvent({
      name: 'Onboarding Beneficiary List Person Added',
      usePrefix: false,
    });
  };

  const hasMissingEmails = companyLegalBeneficiaries.some(
    (beneficiary) => !beneficiary.invitation?.inviteeEmail,
  );

  const deleteSupposedBeneficiary = (supposedBeneficiaryId: string) => {
    const newSet = new Set(deletedSupposedBeneficiaries).add(
      supposedBeneficiaryId,
    );

    saveDeletedSupposedBeneficiaries(newSet);
    setDeletedSupposedBeneficiaries(newSet);

    logEvent({
      name: 'Onboarding Supposed Beneficiary Deleted',
      usePrefix: false,
    });
  };

  const getIsAddedAmountSharesValid: GetIsAddedAmountSharesValidFn = (
    amount,
    companyLegalBeneficiaryId,
  ) => {
    if (isNil(data.company.profile)) {
      throw new Error('isAddedAmountSharesValid called without sharesData');
    }
    if (isNil(data.company.profile.legalNumberOfShares)) {
      throw new Error(
        'isAddedAmountSharesValid called without legalNumberOfShares',
      );
    }

    if (!amount) {
      return true;
    }

    const totalWithoutNew =
      data.company.legalBeneficiaries
        .filter(
          (beneficiary) =>
            beneficiary.companyLegalBeneficiaryId !== companyLegalBeneficiaryId,
        )
        .reduce((sum, { numberOfShares }) => sum + (numberOfShares ?? 0), 0) ??
      0;

    return totalWithoutNew + amount <= data.company.profile.legalNumberOfShares;
  };

  const totalRegisteredShares =
    (includeShares &&
      data.company.legalBeneficiaries.reduce(
        (sum, { numberOfShares }) => sum + (numberOfShares ?? 0),
        0,
      )) ||
    0;

  const hasMissingShareholders =
    includeShares &&
    totalRegisteredShares < (data.company.profile?.legalNumberOfShares ?? 0);

  const hasSupposedBeneficiaries = supposedBeneficiaries.length > 0;

  /**
   * WARNING: you need to retrieve each `formState` property non-conditionally for it to subscribe to changes :pls:
   * @see https://react-hook-form.com/api/useform/formstate/
   */
  const isFormValid = methods.formState.isValid;

  const canContinue =
    isFormValid &&
    !isCreatingBeneficiary &&
    !hasMissingEmails &&
    !hasMissingShareholders &&
    !hasSupposedBeneficiaries;

  return (
    <FormProvider {...methods}>
      <>
        <Component
          applicantType={applicantType}
          applicationType={applicationType}
          beneficiaries={companyLegalBeneficiaries}
          businessName={data.company.profile.legalName}
          canContinue={canContinue}
          companyProfileId={data.company.companyProfileId}
          currentUser={
            data.companyUser.isMandatee ? null : currentUserBeneficiary ?? {}
          }
          data={data}
          getIsAddedAmountSharesValid={getIsAddedAmountSharesValid}
          hasAccountHolderInvitation={hasAccountHolderInvitation(data)}
          hasMissingEmails={hasMissingEmails}
          hasMissingShareholders={hasMissingShareholders}
          header={header}
          includeInvitationRole={data.companyUser.isMandatee}
          includeShares={includeShares}
          isCreatingBeneficiary={isCreatingBeneficiary}
          isExistingCompany={isExistingCompany}
          isSaving={isSaving}
          onAddAnother={onAddAnother}
          onContinue={onContinue}
          onDeleteSupposedBeneficiary={deleteSupposedBeneficiary}
          onEditingNewBeneficiary={() => setIsCreatingBeneficiary(false)}
          onMoralBeneficiaryPress={onMoralBeneficiaryPress}
          sharesData={
            includeShares
              ? {
                  legalNumberOfShares:
                    data.company.profile?.legalNumberOfShares ?? null,
                  legalShareCapital:
                    data.company.profile?.legalShareCapital ?? null,
                  paidUpCapitalPercentage:
                    data.company.capitalDeposit?.paidUpCapitalPercentage ??
                    null,
                }
              : undefined
          }
          supposedBeneficiaries={supposedBeneficiaries}
        />
      </>
    </FormProvider>
  );
};

export default ManageBeneficiariesPage;
